import type { FC, ReactNode } from "react";
import { FiCheck, FiMinus } from "react-icons/fi";
import type { OrderDetailDTO } from "../../components/format";
import { SectionHeading } from "../../components/SectionHeading";

/**
 * ヘルプセンターの「お試し返品キャンペーンについて」 記事。制度詳細 (対象商品 / 対象期間設計の
 * 思想 / 必須同意事項 / お間違いの多い類似商品など) はすべてこちらが SSOT。マイページ内で
 * 重複ページを作らずに外部リンクとして開く。UTM はマイページの注文詳細セクションからの流入を
 * GA で識別するため。
 */
const TRIAL_RETURN_CAMPAIGN_HELP_URL =
  "https://help.survaq-store.com/%E8%BF%94%E5%93%81%E3%83%BB%E4%BA%A4%E6%8F%9B%E3%81%AB%E3%81%A4%E3%81%84%E3%81%A6/4h7Z6n6Xg31t2176zr7mDL/%E3%81%8A%E8%A9%A6%E3%81%97%E8%BF%94%E5%93%81%E3%82%AD%E3%83%A3%E3%83%B3%E3%83%9A%E3%83%BC%E3%83%B3%E3%81%AB%E3%81%A4%E3%81%84%E3%81%A6/7EbA8qbPTwffusqda416RD?utm_source=mypage&utm_medium=order_detail_included_services&utm_campaign=trial_return_campaign";

/**
 * 注文ごとに「自分にはどんな特典 / 保証が付いているか」 を返品申請ページへ進む前に
 * 一覧で見せるセクション。
 *
 * **背景**: お試し返品キャンペーンに関するお問い合わせがあまり減っていない。既存 UI では
 * キャンペーン情報は返品申請ページ (= 発送後でないと開けない) を踏まないと見られず、自分が
 * 対象か / 対象外かを判別する手段が無かった。「対象だと知っていれば使う」 / 「対象外と知って
 * いれば諦める」 ユーザを増やすため、注文詳細で明示する。
 *
 * **キャンペーンの 3 段階表示** (= サーバ側 `campaign.stage` に従う):
 *  - `pre_ingest` (Logiless 未取込): 対象判定不能なのでキャンペーン行を出さない。1年保証のみ
 *    表示。誤って「対象外」 と表示してしまうことを避ける (注文確定後数分で取込完了するため、
 *    顧客がこの状態を見るケースはレア)
 *  - `not_eligible_product`: 対象外商品。グレーアウト + 取り消し線で表示。
 *    詳細ページへのリンクは出す (= 制度自体は伝える)
 *  - `eligible_product`: 対象商品。✅ で表示
 *    - `window.dates` あり (= 出荷済): 「申請可能期間: YYYY/MM/DD 〜 YYYY/MM/DD (発送から
 *      X〜Y 日)」 — 具体的な暦日を出す
 *    - `window.dates` なし (= 未出荷 / 進行中申請): 「発送から X〜Y 日以内に申請可能」 —
 *      日数のみ表示 (= 30 / 90 はタグから確定済なので嘘にならない)
 *    - `window === null` (= 進行中申請ありで期間情報を抑制): 「詳細を見る」 だけ出して期間文言は省略
 *
 * 期間情報は `returnEligibility.campaign.window` をそのまま信頼する (= サーバ側の eligibility
 * ロジックと完全に一致させるため、UI 側で再計算しない)。
 */
export const IncludedServicesSection: FC<{ order: OrderDetailDTO }> = ({ order }) => {
  const campaign = order.returnEligibility.campaign;

  return (
    <section className="mb-10">
      <SectionHeading title="ご注文の特典・保証" />
      <ul className="divide-y divide-neutral-200">
        <ServiceRow
          included={true}
          label="1年保証"
          description="ご購入から1年以内に発生した不具合の交換を承ります。"
        />
        {campaign.stage !== "pre_ingest" && (
          <ServiceRow
            included={campaign.stage === "eligible_product"}
            label="お試し返品キャンペーン"
            description={
              campaign.stage === "eligible_product" ? (
                <CampaignDescription window={campaign.window} />
              ) : (
                "ご注文の商品は対象外です。"
              )
            }
            detailsHref={TRIAL_RETURN_CAMPAIGN_HELP_URL}
          />
        )}
      </ul>
    </section>
  );
};

const CampaignDescription: FC<{
  window: OrderDetailDTO["returnEligibility"]["campaign"]["window"];
}> = ({ window }) => {
  if (!window) {
    // 進行中申請ありなどで期間情報を抑制している状態
    return <>申請可能期間につきましては下のリンク先をご覧ください。</>;
  }
  if (window.dates) {
    return (
      <>
        申請可能期間: {window.dates.start} 〜 {window.dates.end} (発送から {window.minDays}〜
        {window.maxDays} 日)
      </>
    );
  }
  return (
    <>
      ご発送後 {window.minDays}〜{window.maxDays} 日以内に申請いただけます。
      具体的な期間は発送後にご案内いたします。
    </>
  );
};

const ServiceRow: FC<{
  included: boolean;
  label: string;
  description: ReactNode;
  /** 「詳細を見る」 リンク先 (未指定なら出さない)。対象外ユーザにも見せたいので included と独立。 */
  detailsHref?: string;
}> = ({ included, label, description, detailsHref }) => (
  <li className="flex items-start gap-3 py-4">
    <ServiceIcon included={included} />
    <div className="min-w-0 flex-1 space-y-0.5">
      <p
        className={`font-medium text-sm ${included ? "text-neutral-900" : "text-neutral-400 line-through"}`}
      >
        {label}
      </p>
      <p
        className={`text-xs leading-relaxed ${included ? "text-neutral-500" : "text-neutral-400"}`}
      >
        {description}
      </p>
      {detailsHref && (
        <p className="pt-1">
          <a
            href={detailsHref}
            target="_blank"
            rel="noreferrer"
            className="text-neutral-500 text-xs underline underline-offset-4 hover:text-neutral-900"
          >
            詳細を見る (ヘルプセンター)
          </a>
        </p>
      )}
    </div>
  </li>
);

const ServiceIcon: FC<{ included: boolean }> = ({ included }) =>
  included ? (
    <span
      role="img"
      aria-label="ご利用いただけます"
      className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700"
    >
      <FiCheck size={14} strokeWidth={2.5} aria-hidden />
    </span>
  ) : (
    <span
      role="img"
      aria-label="対象外です"
      className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-neutral-400"
    >
      <FiMinus size={14} strokeWidth={2.5} aria-hidden />
    </span>
  );
